import { useBlock, useReadContract } from "wagmi";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";
import { ELECTION_ABI } from "@/contract/abi/election";
import ContractAddress from "@/contract/addresses";

/**
 * Derives voting period state and epoch boundaries from the Election contract.
 *
 * Accepts current cycle as a parameter (provided by useCurrentCycle).
 *
 * Math:
 *   voteEndUnix = startTime + cycle * CYCLE_UNIT + VOTE_PERIOD
 *   nextCycleStartUnix = startTime + (cycle + 1) * CYCLE_UNIT
 */
export function useVotingPeriod(cycle: number | undefined) {
  const {
    data: startTime,
    isLoading: loadingStart,
    isError: errorStart,
  } = useReadContract({
    address: ContractAddress.ELECTION,
    abi: ELECTION_ABI,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "startTime",
    query: { retry: false },
  });

  const {
    data: cycleUnit,
    isLoading: loadingCycleUnit,
    isError: errorCycleUnit,
  } = useReadContract({
    address: ContractAddress.ELECTION,
    abi: ELECTION_ABI,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "CYCLE_UNIT",
    query: { retry: false },
  });

  const {
    data: votePeriod,
    isLoading: loadingVotePeriod,
    isError: errorVotePeriod,
  } = useReadContract({
    address: ContractAddress.ELECTION,
    abi: ELECTION_ABI,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "VOTE_PERIOD",
    query: { retry: false },
  });

  const {
    data: isVotingPeriodRaw,
    isLoading: loadingVoting,
    isError: errorVoting,
    refetch,
  } = useReadContract({
    address: ContractAddress.ELECTION,
    abi: ELECTION_ABI,
    chainId: EXPECTED_CHAIN_ID,
    functionName: "checkVotingPeriod",
    query: { retry: false },
  });

  // Latest block timestamp — chain-anchored "now". Required because the user's
  // browser clock and the chain clock diverge on local Hardhat forks (we
  // `evm_increaseTime` to fast-forward the voting period during testing).
  //
  // No `watch: true` here — wagmi was firing eth_getBlockByNumber every second
  // across every component that mounted useVotingPeriod (ElectionTab + each
  // CandidateCard), saturating the RPC. Vote/withdraw mutations already
  // invalidate the readContract prefix, which refetches this read with the rest.
  const { data: latestBlock } = useBlock({ chainId: EXPECTED_CHAIN_ID });
  const chainNow =
    latestBlock?.timestamp !== undefined
      ? Number(latestBlock.timestamp)
      : undefined;

  const isLoading =
    loadingStart || loadingCycleUnit || loadingVotePeriod || loadingVoting;
  const isError =
    errorStart || errorCycleUnit || errorVotePeriod || errorVoting;

  let voteEndUnix: number | undefined;
  let nextCycleStartUnix: number | undefined;

  if (
    startTime !== undefined &&
    cycleUnit !== undefined &&
    votePeriod !== undefined &&
    cycle !== undefined
  ) {
    const cycleBig = BigInt(cycle);
    voteEndUnix = Number(startTime + cycleBig * cycleUnit + votePeriod);
    nextCycleStartUnix = Number(startTime + (cycleBig + 1n) * cycleUnit);
  }

  // cycleEnded uses chain time, not browser time, to stay correct across both
  // mainnet (where they match) and Hardhat-forked test runs (where they don't).
  const cycleEnded =
    voteEndUnix !== undefined &&
    chainNow !== undefined &&
    chainNow > voteEndUnix;

  return {
    isVotingPeriod: isVotingPeriodRaw,
    voteEndUnix,
    nextCycleStartUnix,
    cycleEnded,
    chainNow,
    isLoading,
    isError,
    refetch,
  };
}
